import * as d3 from 'd3'

import { infield, outfield, positionMap } from './constants'

function sortByVelo(arr) {
    return arr.sort((a, b) => b.metric - a.metric)
}

const radius = 4
const strokeWidth = 1
// const collideForce = radius + strokeWidth

export default class PlayerSwarm {
    constructor(
        data,
        chart,
        player,
        { colorScale, xScale, xScaleDetailed, yScale }
    ) {
        this.chart = chart
        this.player = player
        this.colorScale = colorScale

        this.xScaleDetailed = xScaleDetailed
        this.xScale = xScale
        this.yScale = yScale

        this.radius = radius
        this.smallRadius = radius / 2

        this.getTopThrows(data)

        this.data = data.map((d) => ({
            ...d,
            x: this.xScaleDetailed(d.metric),
            y: this.yScale(positionMap[d.pos]),
            qualified:
                this.groupedThrows[d.pos].ids.includes(d.play_id) ||
                this.supplementalIds.includes(d.play_id),
        }))

        this.simulation = d3
            .forceSimulation(this.data)
            .force(
                'x',
                d3.forceX((d) => this.xScaleDetailed(d.metric)).strength(0.1)
            )
            .force(
                'y',
                d3.forceY((d) => this.yScale(positionMap[d.pos])).strength(0.05)
            )
            .force(
                'collide',
                d3
                    .forceCollide()
                    .radius((d) =>
                        d.qualified
                            ? radius + strokeWidth / 2
                            : (2 * radius) / 3
                    )
                    .strength(0.1)
            )
            .stop()
            .on('tick', this.tick.bind(this))

        this.transition()

        // manually wait to start swarm for now.
        // this.simulation.restart();
    }

    destroy() {
        this.simulation.stop()
        this.data = []
        this.transition()
    }

    getTopThrows(data) {
        const groupedThrows = d3.group(data, (d) => d.pos)

        this.groupedThrows = Array.from(groupedThrows).reduce(
            (acc, [pos, throws]) => {
                const sortedThrows = sortByVelo(throws)
                const posStr = positionMap[pos]
                let percent = 0
                if (infield.includes(posStr) && throws.length >= 75) {
                    percent = 0.05
                } else if (outfield.includes(posStr) && throws.length > 50) {
                    percent = 0.1
                } else if (posStr === '1b' && throws.length >= 100) {
                    percent = 0.01
                }

                const topThrows = sortedThrows.slice(
                    0,
                    Math.round(sortedThrows.length * percent)
                )

                acc[pos] = {
                    topThrows,
                    posStr,
                    pos,
                    ids: topThrows.map((d) => d.play_id),
                }
                return acc
            },
            {}
        )

        this.supplementTopThrows(data)
    }

    // So we want to count throws that qualify one for the IF/OF
    supplementTopThrows(data) {
        const groupedThrows = d3.group(data, (d) => {
            switch (d.pos) {
                case 4:
                case 5:
                case 6:
                    return 'inf'
                case 7:
                case 8:
                case 9:
                    return 'of'
                case 3:
                    return '1b'
                default:
                    return 'unknown'
            }
        })

        // Just want to get all the play_ids that we might be missing
        this.supplementalIds = Array.from(groupedThrows)
            .map(([type, arr]) => {
                arr.sort((a, b) => (a.metric < b.metric ? 1 : -1))

                let percent = 0
                if (type === 'inf') {
                    percent = 0.05
                } else if (type === 'of') {
                    percent = 0.1
                } else if (type === '1b') {
                    percent = 0.01
                }

                const top = arr.slice(0, Math.round(arr.length * percent))

                return top.map((t) => t.play_id)
            })
            .flat()
    }

    transition() {
        this.chart
            .selectAll('circle.player-force')
            .data(this.data, (d) => d.play_id)
            .join(
                (enter) => {
                    const el = enter
                        .append('circle')
                        .attr('class', 'player-force')
                        .attr('fill', (d) =>
                            d.qualified
                                ? this.colorScale(positionMap[d.pos])
                                : '#fff'
                        )
                        .attr('stroke-width', strokeWidth)
                        .attr('stroke', (d) => (d.qualified ? '#ddd' : '#666'))
                        .attr('cx', this.player.x)
                        .attr('cy', this.player.y)
                        .attr('r', (d) =>
                            d.qualified ? this.radius : this.smallRadius
                        )
                        .style('cursor', (d) =>
                            d.qualified ? 'pointer' : 'default'
                        )
                        .on('click', (e, d) => {
                            if (d.qualified) {
                                window.open(
                                    `/sporty-videos?playId=${d.play_id}`,
                                    '_blank'
                                )
                            }
                            e.stopPropagation()
                        })

                    el.transition()
                        .delay((d, i) => i)
                        .attr('cx', (d) => this.xScaleDetailed(d.metric))
                        .attr('cy', (d) => this.yScale(positionMap[d.pos]))
                        .on('end', () => {
                            this.simulation.restart()
                        })

                    return el
                },
                (update) => update,
                (exit) => {
                    const delayDuration = 400
                    const exitSize = exit.size()
                    const delayScale = d3
                        .scaleLinear()
                        .domain([0, exitSize])
                        .range([0, delayDuration])
                    exit.transition()
                        .delay((d, i) => delayScale(i))
                        .attr('cx', () => this.xScale(this.player.arm))
                        .attr('cy', () => this.yScale(this.player.position))
                        .remove()
                }
            )
    }

    tick() {
        this.chart
            .selectAll('circle.player-force')
            .data(this.data, (d) => d.play_id)
            .join(
                (enter) =>
                    enter
                        .append('circle')
                        .attr('class', 'player-force')
                        .attr('cx', (d) => d.x)
                        .attr('cy', (d) => d.y)
                        .attr('r', this.radius)
                        .attr('fill', '#fff'),
                (update) =>
                    update.attr('cx', (d) => d.x).attr('cy', (d) => d.y),
                (exit) => exit.remove()
            )
    }
}
